import React from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { FileDown, Share2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { cn } from '@/lib/utils';
import { formatCurrency, formatCurrencyCompact } from '@/lib/formatters';
import { RiqueDataTable } from '@/components/Rique/RiqueDataTable';
import { exportTablePdf, shareTableContent } from '@/lib/riqueExport';

export type RiqueVisualization =
  | {
      type: 'kpi';
      title?: string;
      items: Array<{ label: string; value: string | number }>;
    }
  | {
      type: 'bar';
      title: string;
      items: Array<{ name: string; value: number }>;
      /** currency = R$ pt-BR; number = contagem/inteiro pt-BR */
      valueFormat?: 'currency' | 'number';
    }
  | {
      type: 'table';
      title: string;
      columns: string[];
      rows: string[][];
    };

export type RiqueDeepLink = { label: string; href: string };

export type RiqueActionDraft = {
  tipo: 'email' | 'whatsapp';
  clienteNome: string;
  draft: string;
  aviso?: string;
};

interface RiqueRichContentProps {
  visualizations?: RiqueVisualization[];
  deepLinks?: RiqueDeepLink[];
  actionDraft?: RiqueActionDraft;
  compact?: boolean;
  expanded?: boolean;
}

function formatBarValue(v: unknown, format: 'currency' | 'number' = 'number'): string {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v ?? '');
  if (format === 'currency') return formatCurrency(n);
  return n.toLocaleString('pt-BR');
}

function formatBarAxis(v: unknown, format: 'currency' | 'number' = 'number'): string {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v ?? '');
  if (format === 'currency') return formatCurrencyCompact(n);
  return n.toLocaleString('pt-BR');
}

function VizExportActions({
  title,
  columns,
  rows,
}: {
  title: string;
  columns: string[];
  rows: string[][];
}) {
  const [sharing, setSharing] = React.useState(false);

  return (
    <div className="flex flex-wrap items-center gap-1">
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-lg border border-border/80 bg-background px-2 py-1 text-[11px] font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground"
        title="Exportar PDF"
        onClick={() => {
          try {
            exportTablePdf(title, columns, rows);
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Falha ao exportar PDF');
          }
        }}
      >
        <FileDown className="h-3 w-3" aria-hidden />
        PDF
      </button>
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-lg border border-border/80 bg-background px-2 py-1 text-[11px] font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground"
        title="Compartilhar"
        disabled={sharing}
        onClick={() => {
          setSharing(true);
          void shareTableContent(title, columns, rows)
            .then((channel) => {
              if (channel === 'clipboard') toast.success('Copiado. Cole onde quiser compartilhar.');
            })
            .catch((e: unknown) => {
              if (e instanceof DOMException && e.name === 'AbortError') return;
              toast.error(e instanceof Error ? e.message : 'Não foi possível compartilhar');
            })
            .finally(() => setSharing(false));
        }}
      >
        <Share2 className="h-3 w-3" aria-hidden />
        {sharing ? '…' : 'Compartilhar'}
      </button>
    </div>
  );
}

export const RiqueRichContent: React.FC<RiqueRichContentProps> = ({
  visualizations = [],
  deepLinks = [],
  actionDraft,
  compact = false,
  expanded = false,
}) => {
  const copyDraft = async () => {
    if (!actionDraft?.draft) return;
    await navigator.clipboard.writeText(actionDraft.draft);
  };

  if (visualizations.length === 0 && deepLinks.length === 0 && !actionDraft) return null;

  const roomy = expanded || !compact;

  return (
    <div className={cn('mt-3 space-y-3', !roomy && 'max-w-xl')}>
      {visualizations.map((viz, idx) => {
        if (viz.type === 'kpi') {
          const kpiCols = ['Indicador', 'Valor'];
          const kpiRows = viz.items.map((item) => [item.label, String(item.value)]);
          const kpiTitle = viz.title ?? 'Indicadores';
          return (
            <div
              key={idx}
              className="rounded-xl border border-border/70 bg-card p-3 shadow-sm sm:p-4"
            >
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-semibold text-foreground">{kpiTitle}</p>
                <VizExportActions title={kpiTitle} columns={kpiCols} rows={kpiRows} />
              </div>
              <div
                className={cn(
                  'grid gap-2',
                  roomy ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4' : 'grid-cols-2'
                )}
              >
                {viz.items.map((item) => (
                  <div
                    key={item.label}
                    className="rounded-lg border border-border/50 bg-muted/25 px-3 py-2.5"
                  >
                    <p className="text-[11px] text-muted-foreground">{item.label}</p>
                    <p className="mt-0.5 text-base font-semibold tabular-nums text-foreground">
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        }

        if (viz.type === 'bar' && viz.items.length > 0) {
          const chartH = roomy ? 220 : 140;
          const valueFormat = viz.valueFormat ?? 'number';
          const barCols = ['Item', 'Valor'];
          const barRows = viz.items.map((item) => [
            item.name,
            formatBarValue(item.value, valueFormat),
          ]);
          return (
            <div
              key={idx}
              className="rounded-xl border border-border/70 bg-card p-3 shadow-sm sm:p-4"
            >
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs font-semibold text-foreground">{viz.title}</p>
                <VizExportActions title={viz.title} columns={barCols} rows={barRows} />
              </div>
              <div className="w-full" style={{ height: chartH }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={viz.items}
                    margin={{
                      top: 8,
                      right: 12,
                      left: valueFormat === 'currency' ? 12 : 4,
                      bottom: roomy ? 48 : 28,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.35} />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: roomy ? 11 : 9 }}
                      interval={0}
                      angle={viz.items.length > 4 ? -28 : 0}
                      textAnchor={viz.items.length > 4 ? 'end' : 'middle'}
                      height={viz.items.length > 4 ? 56 : 28}
                    />
                    <YAxis
                      tick={{ fontSize: roomy ? 11 : 9 }}
                      width={valueFormat === 'currency' ? (roomy ? 72 : 56) : roomy ? 48 : 36}
                      tickFormatter={(v: number) => formatBarAxis(v, valueFormat)}
                    />
                    <Tooltip
                      formatter={(v: number) => [formatBarValue(v, valueFormat), 'Valor']}
                      labelStyle={{ fontWeight: 600 }}
                      contentStyle={{
                        borderRadius: 8,
                        border: '1px solid hsl(var(--border))',
                        fontSize: 12,
                      }}
                    />
                    <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} maxBarSize={48} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        }

        if (viz.type === 'table') {
          return (
            <RiqueDataTable
              key={idx}
              title={viz.title}
              columns={viz.columns}
              rows={viz.rows}
              rowLimit={roomy ? 25 : 8}
              showActions
            />
          );
        }

        return null;
      })}

      {actionDraft ? (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 sm:p-4">
          <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">
            Rascunho {actionDraft.tipo === 'whatsapp' ? 'WhatsApp' : 'E-mail'} — {actionDraft.clienteNome}
          </p>
          <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded-lg bg-background/60 p-2 text-xs text-foreground">
            {actionDraft.draft}
          </pre>
          {actionDraft.aviso ? (
            <p className="mt-1.5 text-[11px] text-muted-foreground">{actionDraft.aviso}</p>
          ) : null}
          <button
            type="button"
            onClick={() => void copyDraft()}
            className="mt-2 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted/60"
          >
            Copiar rascunho
          </button>
        </div>
      ) : null}

      {deepLinks.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {deepLinks.map((link) => (
            <Link
              key={link.href + link.label}
              to={link.href}
              className="rounded-full border border-border/80 bg-background px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/5"
            >
              {link.label}
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
};
